import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { comparisonRows, plans, pricingFaqs } from '../../data/plans';
import type { BillingPeriod, PlanId } from '../../types/plan';
import { PlanRow } from '../../components/PlanRow';
import { useAccount } from '../../contexts/AccountContext';
export function Pricing() {
  const navigate = useNavigate();
  const {
    setPlanId,
    setBillingPeriod
  } = useAccount();
  const [period, setPeriod] = useState<BillingPeriod>('monthly');
  const [domains, setDomains] = useState(3);
  const [wantSsl, setWantSsl] = useState(true);
  const [wantUptime, setWantUptime] = useState(true);
  const checksPerDomain = (wantSsl ? 1 : 0) + (wantUptime ? 1 : 0);
  const servicesNeeded = domains * checksPerDomain;
  const recommendedId = useMemo<PlanId | null>(() => {
    if (servicesNeeded === 0) {
      return null;
    }
    const fit = plans.find(plan => plan.services >= servicesNeeded);
    return fit ? fit.id : 'pro';
  }, [servicesNeeded]);
  const fitPlan = plans.find(plan => plan.id === recommendedId);
  function choose(planId: PlanId) {
    setPlanId(planId);
    setBillingPeriod(period);
    navigate('/signup');
  }
  return <>
      <section className="bg-shell" data-id="element-1634" data-mp="mhm1rqjjkmll">
        <div className="mx-auto grid w-full max-w-page items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-[0.95fr_1.05fr] lg:py-20" data-id="element-1635" data-mp="iexx7v1v21uf">
          <div data-id="element-1636" data-mp="wd5wwaqa7h9a">
            <h1 className="text-[2.5rem] font-semibold leading-[1.05] tracking-[-0.02em] text-white sm:text-[3.1rem]" data-id="element-1637" data-mp="wcmg4xcdaywu">
              From $2 a month.
              <span className="block text-white/50" data-id="element-1638" data-mp="164s47vzi0w6r">Pay for what you watch.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/65" data-id="element-1639" data-mp="114noi86f05hl">
              Plans are sold in <strong className="font-medium text-white" data-id="element-1640" data-mp="1f4f7knbsgc2g">services</strong>.
              One check on one domain is one service — so watching SSL on
              acme.com is one, and pinging acme.com for uptime is another.
            </p>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-white/50" data-id="element-1641" data-mp="7u0p9qawh4zf">
              Count your domains on the right and we will tell you which plan
              you need.
            </p>
          </div>

          <div className="overflow-hidden rounded-card border border-line bg-surface shadow-raised" data-id="element-1642" data-mp="gr19dkqxsgx2">
            <div className="border-b border-line px-6 py-3.5" data-id="element-1643" data-mp="llyqzg2a7ylk">
              <h2 className="text-[13px] font-semibold text-ink" data-id="element-1644" data-mp="qhvzj07b00ns">
                Which plan do I need?
              </h2>
            </div>

            <div className="mt-1 flex items-center justify-between gap-4 border-b border-line px-6 pb-5 pt-5" data-id="element-1645" data-mp="vjv1c5neapim">
              <div data-id="element-1646" data-mp="14iq6deepdtkc">
                <p className="text-sm font-medium text-ink" data-id="element-1647" data-mp="14j8asybire3c">
                  How many sites?
                </p>
                <p className="mt-0.5 text-xs text-ink-soft" data-id="element-1648" data-mp="329jex23x91vb">
                  Domains and subdomains both count
                </p>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-1649" data-mp="36bwhxiraaloe">
                <button type="button" onClick={() => setDomains(n => Math.max(1, n - 1))} aria-label="One fewer domain" className="inline-flex h-8 w-8 items-center justify-center rounded-control border border-line-strong text-ink transition-colors duration-150 ease-snap hover:bg-canvas focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" data-id="element-1650" data-mp="2yrc4rppc1f88">
                  <MinusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1651" data-mp="3crel37hzs0ld" />
                </button>
                <span className="w-10 text-center font-mono text-lg font-medium text-ink" aria-live="polite" data-id="element-1652" data-mp="38noygnkvnt2z">
                  {domains}
                </span>
                <button type="button" onClick={() => setDomains(n => Math.min(30, n + 1))} aria-label="One more domain" className="inline-flex h-8 w-8 items-center justify-center rounded-control border border-line-strong text-ink transition-colors duration-150 ease-snap hover:bg-canvas focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" data-id="element-1653" data-mp="1xacp5x6cmi9f">
                  <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1654" data-mp="3b94upvwy8hbx" />
                </button>
              </div>
            </div>

            <fieldset className="mt-5 px-6" data-id="element-1655" data-mp="2t2wnbvjvcdc">
              <legend className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1656" data-mp="7rwcvc281hh7">
                What should we check on each?
              </legend>
              <div className="mt-2.5 grid gap-2 sm:grid-cols-2" data-id="element-1657" data-mp="3pzc1vfhiadxb">
                {([{
                label: 'SSL expiry',
                value: wantSsl,
                set: setWantSsl
              }, {
                label: 'Uptime',
                value: wantUptime,
                set: setWantUptime
              }] as const).map(check => <button key={check.label} type="button" aria-pressed={check.value} onClick={() => check.set(current => !current)} className={`rounded-control border px-3.5 py-3 text-left text-[13px] font-medium transition-colors duration-150 ease-snap ${check.value ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line-strong text-ink-muted hover:border-ink-soft hover:text-ink'}`} data-id="element-1658" data-mp="26ybf49qoll27">
                    <span className="flex items-center gap-2" data-id="element-1659" data-mp="1x8rx16aslv8e">
                      <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-sm border ${check.value ? 'border-brand bg-brand' : 'border-line-strong'}`} aria-hidden="true" data-id="element-1660" data-mp="1x6f5q2j5a8io">
                        {check.value && <span className="h-1.5 w-1.5 rounded-[1px] bg-white" data-id="element-1661" data-mp="39iu6dikyz4m4" />}
                      </span>
                      {check.label}
                    </span>
                    <span className="mt-1 block pl-[22px] text-xs font-normal text-ink-soft" data-id="element-1662" data-mp="3ifbjusgt3el1">
                      1 service each
                    </span>
                  </button>)}
              </div>
            </fieldset>

            <div className="mx-6 mb-6 mt-5 rounded-control border border-line bg-canvas px-4 py-4" data-id="element-1663" data-mp="2unmtvirgj279">
              {servicesNeeded === 0 ? <p className="text-[13px] text-ink-muted" data-id="element-1664" data-mp="2zkwhqtz775sz">
                  Switch on at least one check to see which plan fits.
                </p> : <>
                  <p className="font-mono text-[13px] text-ink-muted" data-id="element-1665" data-mp="euxeuyx0fmx8">
                    {domains} {domains === 1 ? 'domain' : 'domains'} ×{' '}
                    {checksPerDomain}{' '}
                    {checksPerDomain === 1 ? 'check' : 'checks'} ={' '}
                    <span className="font-medium text-ink" data-id="element-1666" data-mp="fa430djk5ndg">
                      {servicesNeeded} services
                    </span>
                  </p>
                  <p className="mt-2 text-[13px] leading-relaxed text-ink" data-id="element-1667" data-mp="b7hdwke9ugjy">
                    {servicesNeeded > 15 ? 'That is more than Pro’s 15 services — get in touch and we will sort out a custom plan.' : `You need the ${fitPlan?.name} plan — ${fitPlan?.monthlyPrice} a month.`}
                  </p>
                </>}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-canvas" data-id="element-1668" data-mp="1qp12yiha5wzl">
        <div className="mx-auto w-full max-w-page px-5 py-14 sm:px-8" data-id="element-1669" data-mp="1moh6zi8g0zf1">
          <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1670" data-mp="1mp8pbrd150nd">
            <h2 className="text-[1.85rem] font-semibold leading-tight tracking-tight text-ink" data-id="element-1671" data-mp="25yichh2tla6k">
              Choose a plan
            </h2>
            <div className="inline-flex items-center gap-1 rounded-control border border-line-strong bg-surface p-1" data-id="element-1672" data-mp="2a0n5cdlg22s5">
              {(['monthly', 'annual'] as BillingPeriod[]).map(option => <button key={option} type="button" onClick={() => setPeriod(option)} aria-pressed={period === option} className={`rounded-[5px] px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-snap ${period === option ? 'bg-canvas text-ink' : 'text-ink-muted hover:text-ink'}`} data-id="element-1673" data-mp="2145rh2lp3qsp">
                  {option === 'monthly' ? 'Monthly' : 'Yearly · 2 months free'}
                </button>)}
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-1674" data-mp="3mqnkfnscbnlg">
            {plans.map(plan => <PlanRow key={plan.id} plan={plan} period={period} fits={recommendedId === plan.id} onSelect={() => choose(plan.id)} data-id="element-1675" data-mp="3hr177ecii7cn" />)}
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-surface" data-id="element-1676" data-mp="9apo7l4xdlf8">
        <div className="mx-auto w-full max-w-page px-5 py-16 sm:px-8" data-id="element-1677" data-mp="98uzdvqj995v">
          <h2 className="text-[1.85rem] font-semibold leading-tight tracking-tight text-ink" data-id="element-1678" data-mp="4ajqpobz54p8">
            Compare the plans
          </h2>
          <div className="mt-8 overflow-x-auto" data-id="element-1679" data-mp="i99x5ukoogq9">
            <table className="w-full min-w-[640px] border-collapse text-left" data-id="element-1680" data-mp="e9j1pdk7pcfe">
              <caption className="sr-only" data-id="element-1681" data-mp="2k2h9qw8wwk8g">
                Feature comparison across the Starter, Standard and Pro plans
              </caption>
              <thead data-id="element-1682" data-mp="2g2qdid0m9bwj">
                <tr className="border-b border-line-strong" data-id="element-1683" data-mp="2g38lu80jzgkt">
                  <th scope="col" className="w-[34%] pb-3 text-[13px] font-semibold text-ink" data-id="element-1684" data-mp="2v99spmi9m9bw">
                    Feature
                  </th>
                  {plans.map(plan => <th key={plan.id} scope="col" className={`px-3 pb-3 pt-2 text-[13px] font-semibold text-ink ${plan.recommended ? 'rounded-t bg-brand-soft' : ''}`} data-id="element-1685" data-mp="2m9mzhnzow9tx">
                      {plan.name}
                      <span className="ml-1.5 font-mono text-[13px] font-normal text-ink-soft" data-id="element-1686" data-mp="355wfigu5jagk">
                        ${plan.monthlyPrice}
                      </span>
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1687" data-mp="1je706gdclt0">
                {comparisonRows.map(row => <tr key={row.label} className="border-b border-line" data-id="element-1688" data-mp="3suo12pd8kukd">
                    <th scope="row" className="py-3 pr-4 text-[13px] font-normal text-ink-muted" data-id="element-1689" data-mp="432g9lxyy63r">
                      {row.label}
                      {row.hint && <span className="mt-0.5 block text-xs text-ink-soft" data-id="element-1690" data-mp="dyvrn1zybtjy">
                          {row.hint}
                        </span>}
                    </th>
                    {plans.map(plan => <td key={plan.id} className={`px-3 py-3 font-mono text-[13px] text-ink ${plan.recommended ? 'bg-brand-soft' : ''}`} data-id="element-1691" data-mp="rzoj1duyi8iu">
                        {row.value(plan)}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-canvas" data-id="element-1692" data-mp="1ao39o01d3kj4">
        <div className="mx-auto w-full max-w-page px-5 py-16 sm:px-8" data-id="element-1693" data-mp="37qalgxqrezlm">
          <h2 className="text-[1.85rem] font-semibold leading-tight tracking-tight text-ink" data-id="element-1694" data-mp="32rz6k8llxywj">
            Common questions
          </h2>
          <dl className="mt-8 grid gap-x-12 gap-y-6 md:grid-cols-2" data-id="element-1695" data-mp="3gkfdvlljkj39">
            {pricingFaqs.map(faq => <div key={faq.question} className="border-t border-line pt-5" data-id="element-1696" data-mp="3br47flkn64bv">
                <dt className="text-[15px] font-semibold text-ink" data-id="element-1697" data-mp="3qgihkvw1kpsb">
                  {faq.question}
                </dt>
                <dd className="mt-2 text-sm leading-relaxed text-ink-muted" data-id="element-1698" data-mp="3lhytopxw13xb">
                  {faq.answer}
                </dd>
              </div>)}
          </dl>
        </div>
      </section>
    </>;
}